import { rangeFilter } from '../builders/range'
import {
	existsFilter,
	termFilter,
	termsFilter,
	wildcardFilter,
} from '../builders/term'
import { NEGATING_OPERATORS } from '../types/operators'
import { canonicalizeField, isTimestampField, keywordSuffix } from './prefixer'

import type { FilterOperator } from '../types/operators'
import type { QueryClause } from '../types/query'

/**
 * URL-safe filter representation. Lives inside `ft` URL entries.
 */
export type StructuredFilter = {
	f: string
	o: FilterOperator
	v: string | Array<string>
}

export type FilterClauseBucket = {
	filter: Array<QueryClause>
	mustNot: Array<QueryClause>
}

const RANGE_OP_MAP = {
	gt: 'gt',
	lt: 'lt',
	gte: 'gte',
	lte: 'lte',
} as const

function toArray(value: string | Array<string>): Array<string> {
	return Array.isArray(value) ? value : [value]
}

function exactMatchField(field: string): string {
	if (isTimestampField(field)) return field
	return keywordSuffix(field)
}

/**
 * Convert a single structured ft entry into one or more DSL clauses.
 *
 * Negating operators (`neq`, `not_in`) emit clauses that belong in `bool.must_not`.
 * Positive operators emit clauses that belong in `bool.filter`.
 */
export function filterToClause(filter: StructuredFilter): {
	clause: QueryClause | null
	negate: boolean
} {
	const field = canonicalizeField(filter.f)
	const negate = NEGATING_OPERATORS.has(filter.o)

	switch (filter.o) {
		case 'eq':
		case 'neq': {
			const values = toArray(filter.v)
			if (values.length === 0 || values[0] === '') {
				return { clause: null, negate }
			}
			return {
				clause: termFilter(exactMatchField(field), values[0]),
				negate,
			}
		}

		case 'in':
		case 'not_in': {
			const values = toArray(filter.v).filter((v) => v !== '')
			if (values.length === 0) return { clause: null, negate }
			return {
				clause: termsFilter(exactMatchField(field), values),
				negate,
			}
		}

		case 'gt':
		case 'lt':
		case 'gte':
		case 'lte': {
			const values = toArray(filter.v)
			if (values.length === 0 || values[0] === '') {
				return { clause: null, negate }
			}
			const raw = values[0]
			const asNumber = Number(raw)
			const rendered: string | number =
				Number.isFinite(asNumber) && raw.trim() !== '' ? asNumber : raw
			return {
				clause: rangeFilter(field, {
					[RANGE_OP_MAP[filter.o]]: rendered,
				}),
				negate,
			}
		}

		case 'contains': {
			const values = toArray(filter.v)
			if (values.length === 0 || values[0] === '') {
				return { clause: null, negate }
			}
			return {
				clause: wildcardFilter(field, `*${values[0]}*`),
				negate,
			}
		}

		case 'exists':
			return { clause: existsFilter(field), negate }

		default:
			return { clause: null, negate }
	}
}

/**
 * Convert a list of structured filters into `{ filter, mustNot }` buckets
 * ready to drop into a `bool` query.
 */
export function filtersToBuckets(
	filters: ReadonlyArray<StructuredFilter>,
): FilterClauseBucket {
	const buckets: FilterClauseBucket = { filter: [], mustNot: [] }

	for (const f of filters) {
		const { clause, negate } = filterToClause(f)
		if (!clause) continue
		if (negate) {
			buckets.mustNot.push(clause)
		} else {
			buckets.filter.push(clause)
		}
	}

	return buckets
}
